import React from 'react';
import classnames from 'classnames';
import request from 'utils/request_ajax';
import { routes } from 'constants/config';
import { Card, Button } from 'components/ui';
import VideoThumbnail from 'components/VideoResponse/Grading/VideoThumbnail';
import { withZiggeo } from 'components/ui/ZiggeoVideo/ziggeoHelpers';
import FormattedDate from 'components/shared/FormattedDate';
import PreserveFormatting from 'components/shared/PreserveFormatting';

import { FeatureContext } from '../FeatureContext';

import AskForFeedback from './AskForFeedback';
import PreviewModal from './PreviewModal';
import ConfirmDelete from './ConfirmDelete';
import DeleteAlert from './DeleteAlert';

const SCENARIO_TYPES = {
  AUDIO_RECORDING: 'audio_recording',
  SCREEN_RECORDING: 'screen_recording',
  WEBCAM_RECORDING: 'webcam_recording',
  WRITTEN_RESPONSE: 'written_response',
};

const VIDEO_PREVIEW_SCENARIO_TYPES = {
  [SCENARIO_TYPES.AUDIO_RECORDING]: true,
  [SCENARIO_TYPES.SCREEN_RECORDING]: true,
  [SCENARIO_TYPES.WEBCAM_RECORDING]: true,
};

const VideoPreview = ({ onClick, onKeyDown, attempt }) => (
  <div
    className="PreviousAttempt-thumbnail mr-16 mb-16"
    onClick={onClick}
    onKeyDown={onKeyDown}
    tabIndex="0"
    role="button"
  >
    <VideoThumbnail
      mediaThumbnailSrc={attempt.recording.thumbnail}
      questionType={attempt.scenarioType}
    />
  </div>
);

const WrittenPreview = ({ attempt }) => (
  <div className="PreviousAttempt-written mr-16 mb-16">
    <PreserveFormatting>{attempt.answer}</PreserveFormatting>
  </div>
);

const Preview = ({ attempt, onClick, onKeyDown }) => {
  if (VIDEO_PREVIEW_SCENARIO_TYPES[attempt.scenarioType]) {
    return (
      <VideoPreview
        attempt={attempt}
        thumbnail={attempt.recording.thumbnail}
        onClick={onClick}
        onKeyDown={onKeyDown}
      />
    );
  }

  if (attempt.scenarioType === SCENARIO_TYPES.WRITTEN_RESPONSE) {
    return <WrittenPreview attempt={attempt} />;
  }

  return null;
};

class PreviousAttempt extends React.Component {
  state = {
    modalVisible: false,
    deleteError: false,
    confirmDelete: false,
  };

  static contextType = FeatureContext;

  playerRef = React.createRef();

  openModal = () => {
    this.setState({
      modalVisible: true,
    });
  };

  closeModal = () => {
    this.setState({
      modalVisible: false,
    });
  };

  closeDeleteErrorModal = () => {
    this.setState({
      deleteError: false,
    });
  };

  deleteAttempt = () => {
    const { attempt, scenarioId } = this.props;
    request
      .delete(
        routes.ON_DEMAND_PRACTICE.LEARNING.ATTEMPTS.SHOW(
          scenarioId,
          attempt.id,
        ),
      )
      .then(data => {
        if (data.success) {
          this.props.removeAttempt(attempt.id);
        }
      })
      .fail(() => {
        this.setState({ deleteError: true });
      });
  };

  toggleDeleteModal = () => {
    this.setState(state => ({
      confirmDelete: !state.confirmDelete,
    }));
  };

  render() {
    const { modalVisible, deleteError, confirmDelete } = this.state;
    const {
      scenarioId,
      highlighted,
      attempt,
      canRequestFeedback,
      hasZiggeoForAllMedia,
      apiKey,
    } = this.props;

    return (
      <Card
        className={classnames('PreviousAttempt', 'mb-32', {
          'is-highlighted': highlighted,
        })}
      >
        <Card.Content className="PreviousAttempt-content d-f m-fd-c jc-sb px-16 pt-16">
          <Preview
            attempt={attempt}
            onClick={this.openModal}
            onKeyDown={e => e.key === 'Enter' && this.openModal()}
            hasZiggeoForAllMedia={hasZiggeoForAllMedia}
          />
          <div className="d-f f-1 jc-s">
            <div className="mr-16 mb-16">
              <h2 className="PreviousAttempt-title">{attempt.title}</h2>
              <h3 className="PreviousAttempt-prompt text-wrap">
                {attempt.prompt}
              </h3>
              <FormattedDate omitCurrentYear={false} className="h-36 lh-36">
                {attempt.createdAt}
              </FormattedDate>
            </div>
            <div className="PreviousAttempt-actions">
              <Button
                icon="delete"
                className="PreviousAttempt-deleteButton"
                onClick={this.toggleDeleteModal}
              />
            </div>
          </div>
        </Card.Content>
        <AskForFeedback
          scenarioId={scenarioId}
          attempt={attempt}
          caption={t(
            'practice.on_demand_practice.previous_attempts.link_description',
          )}
          canRequestFeedback={canRequestFeedback}
        />
        <PreviewModal
          scenarioId={scenarioId}
          attempt={attempt}
          caption={t(
            'practice.on_demand_practice.previous_attempts.link_description',
          )}
          show={modalVisible}
          prompt={prompt}
          handleDismiss={this.closeModal}
          canRequestFeedback={canRequestFeedback}
          hasZiggeoForAllMedia={hasZiggeoForAllMedia}
          ziggeoApiKey={apiKey}
        />
        {deleteError && (
          <DeleteAlert closeDeleteErrorModal={this.closeDeleteErrorModal} />
        )}
        {confirmDelete && (
          <ConfirmDelete
            toggleDeleteModal={this.toggleDeleteModal}
            deleteAttempt={this.deleteAttempt}
          />
        )}
      </Card>
    );
  }
}

export default withZiggeo(PreviousAttempt);
